import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, Chip, Stack, Typography, useMediaQuery } from '@mui/material';
import ArcadeGameShell from '../ArcadeGameShell';
import { useArcadeAgentSession } from '../useArcadeAgentSession';
import { ManualPlayChrome, useManualPlay, usePlayKeyboard } from '../manualPlay';
import { useGmSession } from '../useGmSession';
import { GAME_BOILERPLATE } from './AGENT_BOILERPLATE';
import { ARCADE } from '../../../utils/config';
import { useManualRunRecorder } from '../useManualRunRecorder';
import SantaDropScene, { SD_BURST_TICKS, buildSantaDropTick } from '../visuals/SantaDropScene';
import {
    createSantaSession,
    deleteSantaSession,
    moveSantaSession,
    MAX_BATCH_TICKS,
    GIFT_POINTS,
    LANE_COUNT,
    MAX_GIFTS,
    MAX_SCORE,
    SLED_START_X,
} from './santaGmClient';
import { gmStartErrorMessage } from '../gmLease';
import { COLOR, GAME_ACCENT } from '../ui/tokens';
import { createTickBatcher, startFixedClock } from '../tickBatcher';

const GAME_ID = 'santa_drop';
const { UPLOAD_URL } = ARCADE.santa_drop;
const ACCENT = GAME_ACCENT.santa_drop;
const HINT = '60 s run · ← → / A D steer · Space / Enter / ↓ drop when the ▼ marker turns gold · Esc pause';
const TICK_MS = 50;

export { GIFT_POINTS, LANE_COUNT, MAX_GIFTS, MAX_SCORE, TICK_MS };

export const LEFT_KEYS = ['ArrowLeft', 'a', 'A'];
export const RIGHT_KEYS = ['ArrowRight', 'd', 'D'];
export const DROP_KEYS = [' ', 'Spacebar', 'Enter', 'ArrowDown', 's', 'S'];

/** Key -> 'left' | 'right' | 'drop' | null. */
export function santaKeyAction(key) {
    if (LEFT_KEYS.includes(key)) return 'left';
    if (RIGHT_KEYS.includes(key)) return 'right';
    if (DROP_KEYS.includes(key)) return 'drop';
    return null;
}

/**
 * Action for the next tick: a queued drop wins once, otherwise the held direction.
 * Returns [action, dropStillQueued].
 */
export function nextSantaAction(held, dropQueued) {
    if (dropQueued) return ['drop', false];
    return [held === 'left' || held === 'right' ? held : 'stay', false];
}

export const emptySantaView = () => ({
    sledPos: SLED_START_X,
    lane: 2,
    dropLead: 28,
    maxSteps: 1200,
    aimHouseId: null,
    endReason: null,
    houses: [],
    gifts: [],
    tick: 0,
    score: 0,
    dropped: 0,
    landed: 0,
    combo: 0,
    bestCombo: 0,
    seed: null,
    isGameOver: false,
    message: '',
});

export function santaViewFromGm(data = {}) {
    return {
        sledPos: typeof data.sled_pos === 'number' ? data.sled_pos : SLED_START_X,
        lane: typeof data.sled_x === 'number' ? data.sled_x : 2,
        dropLead: typeof data.drop_lead === 'number' ? data.drop_lead : 28,
        maxSteps: data.meta?.max_steps || 1200,
        aimHouseId: data.aim_house_id ?? null,
        endReason: data.end_reason ?? null,
        houses: Array.isArray(data.houses) ? data.houses : [],
        gifts: Array.isArray(data.gifts_in_air) ? data.gifts_in_air : [],
        tick: typeof data.tick === 'number' ? data.tick : (data.step_count || 0),
        score: typeof data.score === 'number' ? data.score : 0,
        dropped: typeof data.gifts_dropped === 'number' ? data.gifts_dropped : 0,
        landed: typeof data.gifts_landed === 'number' ? data.gifts_landed : 0,
        combo: data.combo || 0,
        bestCombo: data.best_combo || 0,
        seed: data.meta?.seed ?? null,
        isGameOver: Boolean(data.is_game_over),
        message: data.message || '',
    };
}

/** Keep land/miss bursts alive for a few ticks after the GM reports them. */
export function ageBursts(bursts, events = [], tick = 0) {
    const kept = bursts.filter((b) => tick - b.born < SD_BURST_TICKS);
    events.forEach((e) => {
        if (e && (e.type === 'land' || e.type === 'miss')) kept.push({ type: e.type, x: e.x, y: e.y, born: tick });
    });
    return kept;
}

const formatSantaScore = (e) => {
    const pts = e.score ?? 0;
    if (e.gifts_landed != null && e.gifts_dropped != null) return `${pts} (${e.gifts_landed}/${e.gifts_dropped})`;
    return String(pts);
};

/** Fixed one-line status under the scene so the touch buttons never move. */
export const STATUS_LINE_SX = {
    color: COLOR.textMuted,
    mt: 1,
    fontSize: '0.85rem',
    lineHeight: '1.4em',
    height: '1.4em',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
};

/** End-screen heading: the reason the run ended. */
export function santaOverTitle(view) {
    if (view.landed === MAX_GIFTS) return 'Perfect delivery!';
    if (view.endReason === 'time') return "Time's up";
    if (view.endReason === 'gifts') return 'All gifts dropped';
    return 'Run complete';
}

const TOUCH_IDLE = { bgcolor: 'rgba(255,255,255,0.06)', borderColor: 'rgba(255,255,255,0.25)' };

/** Touch buttons: no ripple, tap highlight or sticky :hover/:active/focus look on phones. */
export const touchBtnSx = {
    minWidth: 64,
    minHeight: 52,
    fontSize: '1.5rem',
    color: 'white',
    ...TOUCH_IDLE,
    touchAction: 'none',
    userSelect: 'none',
    WebkitUserSelect: 'none',
    WebkitTapHighlightColor: 'transparent',
    WebkitTouchCallout: 'none',
    outline: 'none',
    '&:hover, &:active, &:focus, &.Mui-focusVisible': TOUCH_IDLE,
    '@media (hover: none)': { '&:hover': TOUCH_IDLE },
};

const dropBtnSx = {
    ...touchBtnSx,
    minWidth: 84,
    borderColor: ACCENT,
    '&:hover, &:active, &:focus, &.Mui-focusVisible': { ...TOUCH_IDLE, borderColor: ACCENT },
    '@media (hover: none)': { '&:hover': { ...TOUCH_IDLE, borderColor: ACCENT } },
};

const blurTarget = (e) => {
    if (e && e.currentTarget && typeof e.currentTarget.blur === 'function') e.currentTarget.blur();
};

const SantaDrop = () => {
    const play = useManualPlay();
    const [view, setView] = useState(emptySantaView);
    const [bursts, setBursts] = useState([]);
    const viewRef = useRef(view);
    const heldRef = useRef('stay');
    const dropQueuedRef = useRef(false);
    const phaseRef = useRef(play.phase);
    const batcherRef = useRef(null);

    const session = useArcadeAgentSession({ gameId: GAME_ID, uploadUrl: UPLOAD_URL });
    const recorder = useManualRunRecorder({ gameId: GAME_ID, session });
    const { sessionIdRef, createFreshSession, clearSession, setBusy } = useGmSession({
        createSession: createSantaSession,
        deleteSession: deleteSantaSession,
    });
    const { startGame: playStart, endGame: playEnd, markOver, togglePause, phase } = play;

    useEffect(() => { phaseRef.current = phase; }, [phase]);

    const applyGm = useCallback((data) => {
        const next = santaViewFromGm(data);
        viewRef.current = next;
        setView(next);
        setBursts((prev) => ageBursts(prev, data.events, next.tick));
        return next;
    }, []);

    const resetInput = useCallback(() => {
        heldRef.current = 'stay';
        dropQueuedRef.current = false;
    }, []);

    const startGame = useCallback(async () => {
        try {
            setBusy(true);
            const data = await createFreshSession();
            setBursts([]);
            const next = applyGm(data);
            recorder.reset({ init_state: data.init_state || { sled_x: next.lane, sled_pos: next.sledPos, seed: next.seed } });
            resetInput();
            playStart();
        } catch (err) {
            console.error(err);
            setView((v) => ({ ...v, message: gmStartErrorMessage(err) }));
        } finally {
            setBusy(false);
        }
    }, [applyGm, createFreshSession, playStart, recorder, resetInput, setBusy]);

    const endGame = useCallback(async () => {
        const finalScore = viewRef.current.score;
        // Stop the clock and input before deleting the session so no further
        // /move goes out after End (prod smoke still saw one keypress → 404).
        resetInput();
        phaseRef.current = 'ended';
        if (batcherRef.current) {
            batcherRef.current.stop();
            batcherRef.current = null;
        }
        playEnd({ score: finalScore });
        await clearSession();
        const next = emptySantaView();
        viewRef.current = next;
        setView(next);
        setBursts([]);
    }, [clearSession, playEnd, resetInput]);

    // Steady 50 ms clock: one action per tick, whatever the latency. Ticks
    // that pass while a request is in flight go out together with the next
    // one (applied in order by the GM, one deterministic tick each).
    const handleResult = useCallback(async (sid, data, actions) => {
        if (sessionIdRef.current !== sid) return; // ended or restarted meanwhile
        const next = applyGm(data);
        const steps = Array.isArray(data.steps) && data.steps.length
            ? data.steps
            : actions.map((a) => buildSantaDropTick(data, a));
        steps.forEach((step) => recorder.recordTick(step));
        if (next.isGameOver) {
            if (batcherRef.current) batcherRef.current.stop();
            markOver();
            recorder.finalizeAndUpload({
                score: next.score,
                extra: {
                    gifts_dropped: next.dropped,
                    gifts_landed: next.landed,
                    best_combo: next.bestCombo,
                    seed: next.seed,
                    step_count: next.tick,
                },
            });
            await clearSession();
        }
    }, [applyGm, clearSession, markOver, recorder, sessionIdRef]);
    // The clock/batcher effect must not restart when these callbacks change.
    const handleResultRef = useRef(handleResult);
    useEffect(() => { handleResultRef.current = handleResult; }, [handleResult]);

    const tick = useCallback(() => {
        if (phaseRef.current !== 'playing') return;
        if (viewRef.current.isGameOver || !batcherRef.current) return;
        const [action, stillQueued] = nextSantaAction(heldRef.current, dropQueuedRef.current);
        if (batcherRef.current.push(action)) dropQueuedRef.current = stillQueued;
    }, []);

    useEffect(() => {
        if (phase !== 'playing') return undefined;
        const sid = sessionIdRef.current;
        if (!sid) return undefined;
        const batcher = createTickBatcher({
            send: (actions) => moveSantaSession(sid, actions),
            maxBatch: MAX_BATCH_TICKS,
            onResult: (data, actions) => { handleResultRef.current(sid, data, actions); },
            onError: (err) => console.warn('Santa Drop GM tick failed', err),
            onDrop: (action, info) => console.warn('Santa Drop tick dropped', action, info),
        });
        batcherRef.current = batcher;
        const stopClock = startFixedClock({ intervalMs: TICK_MS, onTick: tick });
        return () => {
            stopClock();
            batcher.stop();
            if (batcherRef.current === batcher) batcherRef.current = null;
        };
    }, [phase, tick, sessionIdRef]);

    const onKey = useCallback((e) => {
        const action = santaKeyAction(e.key);
        if (!action) return;
        e.preventDefault();
        if (action === 'drop') {
            if (!e.repeat) dropQueuedRef.current = true;
        } else {
            heldRef.current = action;
        }
    }, []);

    useEffect(() => {
        const up = (e) => {
            const action = santaKeyAction(e.key);
            if (!action) return;
            // Stop a focused button (Pause / End Game) from also "clicking" on Space/Enter keyup.
            if (phaseRef.current === 'playing') e.preventDefault();
            if (action !== 'drop' && heldRef.current === action) heldRef.current = 'stay';
        };
        window.addEventListener('keyup', up);
        return () => window.removeEventListener('keyup', up);
    }, []);

    usePlayKeyboard({ phase, togglePause, onKey });

    const hold = (dir) => (e) => {
        e.preventDefault();
        heldRef.current = dir;
    };
    const release = (dir) => (e) => {
        if (heldRef.current === dir) heldRef.current = 'stay';
        blurTarget(e);
    };
    const pressDrop = (e) => {
        e.preventDefault();
        if (phaseRef.current === 'playing') dropQueuedRef.current = true;
    };
    const touchProps = (dir) => ({
        onPointerDown: hold(dir),
        onPointerUp: release(dir),
        onPointerLeave: release(dir),
        onPointerCancel: release(dir),
        onContextMenu: (e) => e.preventDefault(),
        disableRipple: true,
        disableFocusRipple: true,
        disableTouchRipple: true,
    });
    const smallScreen = useMediaQuery('(max-width:600px)');
    const playing = phase === 'playing';
    const giftsLeft = MAX_GIFTS - view.dropped;

    return (
        <ArcadeGameShell
            title="Santa"
            titleAccent="Drop"
            subtitle={`Drop gifts down the chimneys — ${MAX_GIFTS} gifts, +${GIFT_POINTS} each, max ${MAX_SCORE}`}
            accentColor={ACCENT}
            boilerplate={GAME_BOILERPLATE.santa_drop}
            session={session}
            formatScore={formatSantaScore}
        >
            <ManualPlayChrome
                phase={phase}
                result={play.result}
                keyboardStart={false}
                onStart={startGame}
                onPause={togglePause}
                onEnd={endGame}
                accentColor={ACCENT}
                controlsHint={HINT}
                overTitle={santaOverTitle(view)}
                won={view.landed === MAX_GIFTS}
                overBlur={false}
                overContent={(
                    <Box data-testid="santa-over-summary" sx={{ textAlign: 'center', color: '#fff' }}>
                        <Typography sx={{ fontSize: '1.6rem', fontWeight: 700, color: ACCENT }}>
                            {view.score} points
                        </Typography>
                        <Typography sx={{ fontSize: '1rem', color: COLOR.text }}>
                            {view.landed}/{MAX_GIFTS} delivered · best combo x{view.bestCombo}
                        </Typography>
                    </Box>
                )}
            >
                <Box sx={{ p: { xs: 1, sm: 2 } }}>
                    <Stack direction="row" spacing={1} useFlexGap sx={{ mb: 1.5, flexWrap: 'wrap' }}>
                        <Chip data-testid="santa-chip-score" label={`Score: ${view.score}`} size="small" sx={{ color: ACCENT, borderColor: ACCENT }} variant="outlined" />
                        <Chip data-testid="santa-chip-landed" label={`Landed: ${view.landed}/${view.dropped}`} size="small" variant="outlined" sx={{ color: COLOR.textMuted, borderColor: COLOR.borderStrong }} />
                        <Chip data-testid="santa-chip-left" label={`Gifts left: ${giftsLeft}`} size="small" variant="outlined" sx={{ color: COLOR.textMuted, borderColor: COLOR.borderStrong }} />
                        <Chip
                            data-testid="santa-chip-combo"
                            label={`Combo x${view.combo}`}
                            size="small"
                            sx={{ bgcolor: 'rgba(255,209,102,0.15)', color: '#ffd166', opacity: view.combo > 1 ? 1 : 0.35 }}
                        />
                    </Stack>
                    <Box
                        data-testid="santa-playfield"
                        sx={{ position: 'relative', touchAction: playing ? 'none' : 'auto', overscrollBehavior: 'contain' }}
                    >
                        <SantaDropScene
                            houses={view.houses}
                            gifts={view.gifts}
                            sledPos={view.sledPos}
                            dropLead={view.dropLead}
                            showAim={giftsLeft > 0}
                            maxSteps={view.maxSteps}
                            aimHouseId={view.aimHouseId}
                            markerScale={smallScreen ? 1.7 : 1}
                            tick={view.tick}
                            bursts={bursts.map((b) => ({ ...b, age: view.tick - b.born }))}
                        />
                    </Box>
                    <Typography
                        data-testid="santa-status-line"
                        sx={STATUS_LINE_SX}
                        title={view.message}
                    >
                        {view.message || '\u00a0'}
                    </Typography>
                    <Stack
                        direction="row"
                        spacing={1.5}
                        justifyContent="center"
                        sx={{ mt: 1, touchAction: 'none' }}
                        aria-label="Touch controls"
                    >
                        <Button variant="outlined" aria-label="Move left" disabled={!playing} sx={touchBtnSx} {...touchProps('left')}>◀</Button>
                        <Button variant="outlined" aria-label="Drop gift" disabled={!playing} sx={dropBtnSx} onPointerDown={pressDrop} onPointerUp={blurTarget} onContextMenu={(e) => e.preventDefault()} disableRipple disableFocusRipple disableTouchRipple>🎁</Button>
                        <Button variant="outlined" aria-label="Move right" disabled={!playing} sx={touchBtnSx} {...touchProps('right')}>▶</Button>
                    </Stack>
                </Box>
            </ManualPlayChrome>
        </ArcadeGameShell>
    );
};

export default SantaDrop;
